/**
 * AAC encoders prepend "priming" silent samples to every encoded stream (MDCT window warm-up). When
 * decoded into an AudioBuffer the priming sits at the start as ~46 ms (at 44.1 kHz) of silent or
 * garbage samples before the actual audio content. Playing the buffer from sample 0 produces an
 * audible delay on every one-shot and a click on every loop iteration.
 *
 * The constant assumes FFmpeg's native AAC encoder (2048 samples). Apple's AAC encoder uses 2112 —
 * if the audio pipeline switches encoder, update this. Divided by `buffer.sampleRate` per call so
 * the offset is correct whether the source file is 44.1 kHz or 48 kHz.
 */
export const AAC_PRIMING_SAMPLES = 2048;

export interface OneShotParams {
  id: string;
  buffer: AudioBuffer;
  destination: AudioNode;
  priority: number;
  protectFromSteal: boolean;
  stopOnSlam: boolean;
  playbackRate?: number;
  gain?: number;
  /** Fired when the source ends naturally OR is stopped through the returned `stop()`. */
  onEnded?: () => void;
}

interface Voice {
  id: string;
  startedAtMs: number;
  priority: number;
  protectFromSteal: boolean;
  stopOnSlam: boolean;
  stop: () => void;
}

/**
 * The one-shot voices of the engine: each play is a buffer source (with its own gain when asked),
 * tracked until it ends. Over the voice cap the lowest-priority, oldest unprotected voice is
 * stolen; a slam stops every voice flagged for it.
 */
export class OneShotVoices {
  private voices: Voice[] = [];

  public constructor(
    private readonly maxConcurrent: number,
    /** Told once per voice stolen over the cap (the dev snapshot counts them). */
    private readonly onStolen: () => void = () => {},
  ) {}

  public play(ctx: AudioContext, params: OneShotParams): { stop: () => void } {
    const node = ctx.createBufferSource();
    node.buffer = params.buffer;
    node.playbackRate.value = params.playbackRate ?? 1;

    const useGainNode = typeof params.gain === "number" && Number.isFinite(params.gain) && params.gain >= 0 && params.gain !== 1;
    let gainNode: GainNode | null = null;
    if (useGainNode) {
      gainNode = ctx.createGain();
      gainNode.gain.value = params.gain as number;
      node.connect(gainNode);
      gainNode.connect(params.destination);
    } else {
      node.connect(params.destination);
    }

    const stop = (): void => {
      try {
        node.stop();
      } catch {}
      try {
        node.disconnect();
      } catch {}
      if (gainNode) {
        try {
          gainNode.disconnect();
        } catch {}
      }
    };

    this.voices.push({ id: params.id, startedAtMs: performance.now(), priority: params.priority, protectFromSteal: params.protectFromSteal, stopOnSlam: params.stopOnSlam, stop });
    node.onended = () => {
      this.voices = this.voices.filter((v) => v.stop !== stop);
      params.onEnded?.();
    };

    // Skip the AAC priming silence; a buffer shorter than the priming window starts at 0.
    const primingOffsetSeconds = AAC_PRIMING_SAMPLES / params.buffer.sampleRate;
    node.start(0, primingOffsetSeconds < params.buffer.duration ? primingOffsetSeconds : 0);
    this.enforceCap();
    return { stop };
  }

  public stopOnSlam(): void {
    for (const voice of [...this.voices]) {
      if (voice.stopOnSlam && !voice.protectFromSteal) voice.stop();
    }
  }

  public get count(): number {
    return this.voices.length;
  }

  private enforceCap(): void {
    if (this.voices.length <= this.maxConcurrent) return;
    const candidates = this.voices.filter((v) => !v.protectFromSteal).sort((a, b) => a.priority - b.priority || a.startedAtMs - b.startedAtMs);
    while (this.voices.length > this.maxConcurrent && candidates.length > 0) {
      const victim = candidates.shift()!;
      victim.stop();
      this.voices = this.voices.filter((v) => v !== victim);
      this.onStolen();
    }
  }
}
